<template>
  <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-8">
    <div class="max-w-[1600px] mx-auto">
      <div class="flex items-center gap-2 pb-[25px]">
        <div class="flex gap-2 overflow-x-auto custom-scrollbar min-w-0 flex-1">
          <button
            v-for="f in filters"
            :key="f.value"
            type="button"
            class="px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors shrink-0"
            :class="activeFilter === f.value
              ? 'bg-[#1c4848] text-white shadow-md'
              : 'bg-white border border-[#1c4848]/10 text-[#1c4848]/60 hover:bg-[#fafbfc] hover:text-[#1c4848]'"
            @click="activeFilter = f.value"
          >
            {{ f.label }}
          </button>
        </div>
        <div class="shrink-0">
          <LanguageFilterDropdown v-model="selectedLanguageId" />
        </div>
      </div>
      <div v-if="audioStore.listLoading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <AudioContentCardSkeleton v-for="i in 6" :key="i" />
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <AudioContentCard
          v-for="item in filteredList"
          :key="item.id"
          :item="item"
          @play="$emit('play', item)"
          @edit="$emit('edit', item)"
          @delete="onDelete"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useAudioContentsStore } from '@/store/audio-contents/index.js';
import AudioContentCard from '@/components/audio-content/AudioContentCard.vue';
import AudioContentCardSkeleton from '@/components/audio-content/AudioContentCardSkeleton.vue';
import LanguageFilterDropdown from '@/components/LanguageFilterDropdown.vue';

const props = defineProps({ searchQuery: { type: String, default: '' } });
defineEmits(['play', 'edit']);

const audioStore = useAudioContentsStore();

const activeFilter = ref('');
const selectedLanguageId = ref('');

async function fetchWithFilters() {
  const params = selectedLanguageId.value ? { language_id: selectedLanguageId.value } : {};
  await audioStore.fetchList(params);
}
await fetchWithFilters();

const filters = [
  { value: '', label: 'All Content' },
  { value: 'story', label: 'Islamic Stories' },
  { value: 'nasheed', label: 'Nasheeds' },
  { value: 'calm_sound', label: 'Calm Sounds' },
];

const filteredList = computed(() => {
  let list = audioStore.list;
  if (activeFilter.value) list = list.filter((x) => x.type === activeFilter.value);
  const q = props.searchQuery?.trim()?.toLowerCase();
  if (q) list = list.filter((x) => (x.title || '').toLowerCase().includes(q));
  return list;
});

watch(selectedLanguageId, fetchWithFilters);

async function onDelete(item) {
  if (!item?.id) return;
  const title = item.title ? `"${item.title}"` : 'this item';
  if (!confirm(`Delete ${title}? This cannot be undone.`)) return;
  try {
    await audioStore.delete(item.id);
  } catch {
    alert('Failed to delete. Please try again.');
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(28, 72, 72, 0.1); border-radius: 20px; }
.custom-scrollbar:hover::-webkit-scrollbar-thumb { background-color: rgba(28, 72, 72, 0.2); }
</style>
